/* eslint-disable */
export type TDebounced<T extends Function> = T & {
  flush(): void;
  clear(): void;
};

export const debounce: {
  <T extends Function>(delay: number, immediate: boolean, cb: T): TDebounced<T>;
  <T extends Function>(delay: number, cb: T): TDebounced<T>;

  decorator(
    delay: number,
    immediate?: boolean,
  ): (target: Object, propName: string, propDesc?: PropertyDescriptor) => PropertyDescriptor;
} = function debounce(delay: number, immediate: boolean | Function, cb?: Function): TDebounced<Function> {
  if (typeof immediate == 'function') {
    cb = immediate;
    immediate = false;
  }

  let timestamp: number;
  let lastExec = 0;
  let timeoutID: number | null | undefined;
  let context: any;
  let args: IArguments | null;
  let result: any;

  function later() {
    const now = Date.now();

    if (now - timestamp < delay) {
      timeoutID = window.setTimeout(later, delay - (now - timestamp));
    } else {
      lastExec = now;
      timeoutID = null;
      result = cb!.apply(context, args);
      context = args = null;
    }
  }

  const debounced: TDebounced<Function> = function debounced(this: any) {
    timestamp = Date.now();

    if (immediate && timestamp - lastExec >= delay) {
      lastExec = timestamp;

      if (timeoutID) {
        clearTimeout(timeoutID);

        timeoutID = null;
        result = cb!.apply(context, args);
        context = args = null;
      } else {
        result = cb!.apply(this, arguments);
      }
    } else {
      context = this;
      args = arguments;

      if (!timeoutID) {
        timeoutID = window.setTimeout(later, delay);
      }
    }

    return result;
  } as any;

  debounced.flush = () => {
    if (timeoutID) {
      clearTimeout(timeoutID);

      lastExec = Date.now();
      timeoutID = null;
      result = cb!.apply(context, args);
      context = args = null;
    }
  };

  debounced.clear = () => {
    if (timeoutID) {
      clearTimeout(timeoutID);

      timeoutID = null;
      context = args = null;
    }
  };

  return debounced;
} as any;

debounce.decorator = (delay, immediate) => {
  return (target, propName, propDesc) => {
    if (!propDesc) {
      propDesc = Object.getOwnPropertyDescriptor(target, propName)!;
    }

    return {
      configurable: true,
      enumerable: propDesc.enumerable,

      get: function () {
        const debounced = debounce(delay, immediate || false, propDesc!.value);

        Object.defineProperty(this, propName, {
          configurable: true,
          enumerable: propDesc!.enumerable,
          writable: propDesc!.writable,
          value: debounced,
        });

        return debounced;
      },
    };
  };
};

//

export type TThrottled<T extends Function> = T & {
  flush(): void;
  clear(): void;
};

export const throttle: {
  <T extends Function>(delay: number, noTrailing: boolean, cb: T): TThrottled<T>;
  <T extends Function>(delay: number, cb: T): TThrottled<T>;

  decorator(
    delay: number,
    noTrailing?: boolean,
  ): (target: Object, propName: string, propDesc?: PropertyDescriptor) => PropertyDescriptor;
} = function throttle(delay: number, noTrailing: boolean | Function, cb?: Function): TThrottled<Function> {
  if (typeof noTrailing == 'function') {
    cb = noTrailing;
    noTrailing = false;
  }

  let lastExec = 0;
  let timeoutID: number | null | undefined;
  let context: any;
  let args: IArguments | null;
  let result: any;

  function later() {
    lastExec = Date.now();
    timeoutID = null;
    result = cb!.apply(context, args);
    context = args = null;
  }

  const throttled: TThrottled<Function> = function throttled(this: any) {
    const now = Date.now();

    if (now - lastExec >= delay) {
      lastExec = now;

      if (timeoutID) {
        clearTimeout(timeoutID);

        timeoutID = null;
        result = cb!.apply(context, args);
        context = args = null;
      } else {
        result = cb!.apply(this, arguments);
      }
    } else {
      if (!noTrailing) {
        context = this;
        args = arguments;

        if (!timeoutID) {
          timeoutID = window.setTimeout(later, delay - (now - lastExec));
        }
      }
    }

    return result;
  } as any;

  throttled.flush = () => {
    if (timeoutID) {
      clearTimeout(timeoutID);

      lastExec = Date.now();
      timeoutID = null;
      result = cb!.apply(context, args);
      context = args = null;
    }
  };

  throttled.clear = () => {
    if (timeoutID) {
      clearTimeout(timeoutID);

      timeoutID = null;
      context = args = null;
    }
  };

  return throttled;
} as any;

throttle.decorator = (delay, noTrailing) => {
  return (target, propName, propDesc) => {
    if (!propDesc) {
      propDesc = Object.getOwnPropertyDescriptor(target, propName)!;
    }

    return {
      configurable: true,
      enumerable: propDesc.enumerable,

      get: function () {
        const throttled = throttle(delay, noTrailing || false, propDesc!.value);

        Object.defineProperty(this, propName, {
          configurable: true,
          enumerable: propDesc!.enumerable,
          writable: propDesc!.writable,
          value: throttled,
        });

        return throttled;
      },
    };
  };
};
